/* Copyright (c) Trimble Inc. All rights reserved. */

import { getEnvironmentEndpoints } from '../env.config';
import {
  EnvironmentEndpoints,
  type AuthConfig,
  type Environment,
} from '../models/AuthConfig.model';

export interface Params {
  clientId?: string;
  environment?: Environment;
  scopes?: readonly string[];
}

export interface LoginConfig {
  authorizationEndpoint: string;
  tokenEndpoint: string;
  clientId: string;
  scopes: readonly string[];
  environment: Environment;
}
/**
 * Authentication Store
 * Manages authentication configuration and state
 */
class AuthConfigStoreImpl {
  private config: AuthConfig | null = null;
  private clientId?: string;
  private environment?: Environment;
  private scopes?: readonly string[];

  /**
   * Configure authentication with provided parameters
   */
  configure(params: {
    clientId: string;
    environment: Environment;
    scopes: readonly string[];
  }): AuthConfig {
    // Store raw parameters
    this.clientId = params.clientId;
    this.environment = params.environment;
    this.scopes = params.scopes;

    // Create and validate auth configuration
    this.config = {
      clientId: params.clientId,
      environment: params.environment,
      scopes: params.scopes,
    };
    return this.config;
  }

  /**
   * Get current auth configuration
   */
  getAuthConfig(): AuthConfig | null {
    return this.config;
  }

  /**
   * Get raw configuration parameters
   */
  getParams(): Params {
    return {
      clientId: this.clientId,
      environment: this.environment,
      scopes: this.scopes,
    };
  }

  /**
   * Check if authentication is configured
   */
  isConfigured(): boolean {
    return this.config !== null;
  }

  /**
   * Get environment endpoints
   */
  getEndpoints(): EnvironmentEndpoints | null {
    if (!this.environment) {
      return null;
    }
    return getEnvironmentEndpoints(this.environment);
  }

  /**
   * Get login configuration for URL construction
   */
  getLoginConfig(): LoginConfig | null {
    if (!this.config || !this.environment) {
      return null;
    }

    const endpoints = getEnvironmentEndpoints(this.environment);
    return {
      authorizationEndpoint: endpoints.AUTHORIZATION_ENDPOINT,
      tokenEndpoint: endpoints.TOKEN_ENDPOINT,
      clientId: this.config.clientId,
      scopes: [...this.config.scopes],
      environment: this.environment,
    };
  }

  /**
   * Clear all configuration
   */
  clear(): void {
    this.config = null;
    this.clientId = undefined;
    this.environment = undefined;
    this.scopes = undefined;
  }
}

// Export singleton instance
export const AuthConfigStore = new AuthConfigStoreImpl();
